/**
 * presetModality.js — ГАСИТЬ 3D-ПРЕСЕТЫ ЧУЖОЙ МОДАЛЬНОСТИ. [12.0.34]
 *
 * Правило — core/presetModalityCore.js. Здесь: найти у кнопки её пресет
 * (карта кнопок VRT_PRESET_MAP → пресет → псевдонимы → поле modality) и
 * погасить или вернуть кнопку.
 *
 * Гасится атрибутом disabled: все 85 кнопок пресетов — настоящие <button>,
 * поэтому не срабатывает ни щелчок, ни программный .click() из голосовых
 * команд, палитры и радиального меню. Подсказка кнопки дополняется причиной.
 *
 * [12.0.35] ДВА ВХОДА, А НЕ ОДИН С null.
 *   · отметитьПресетыПоМодальности(dicom) — серия ОТКРЫТА. Зовёт updateOverlays.
 *     Пустой тег здесь значит «модальность неизвестна», а не «серии нет»:
 *     у серии без тега series.modality бывает и null, и undefined.
 *   · погаситьПресетыБезИсследования() — исследования НЕТ: при запуске
 *     программы (initSidebarBindings) и при закрытии исследования
 *     (uiManager). Гаснут все пресеты, универсальные тоже.
 * В 12.0.34 оба случая шли через отметитьПресетыПоМодальности(null), и
 * закрытое исследование возвращало все пресеты в строй — владелец поймал.
 * Отдельное имя нужно, чтобы null из series.modality никогда не читался как
 * «исследования нет» и не гасил всё на настоящем снимке.
 *
 * [12.0.38] ТРИ СОРТА КНОПОК, А НЕ ОДИН. Владелец: «при закрытии всех
 * исследований кнопки всех пресетов должны быть деактивированы, а у тебя три
 * кнопки всё равно активны». Это «MR Кожа», «MR Ткань ½», «MR MIP»: у них
 * data-mr3d, а не data-render-preset, и гашение их не видело с 12.0.34.
 *   · data-render-preset — 3D-пресеты, модальность из пресета;
 *   · data-mr3d          — 3D-пресеты МРТ (applyMR3DPreset), модальность 'mri';
 *   · .preset-chip[data-ww], .preset-chip[data-wl-auto] — пресеты окна
 *     (правая панель и мобильный ящик). Им модальность не задана: они гаснут
 *     только когда исследования нет.
 *
 * Порядок при открытии серии безопасен: uiManager зовёт updateOverlays ДО
 * autoPresetByModality, так что автопресет жмёт уже включённую кнопку
 * (погашенная .click() не отработала бы).
 *
 * EXPORTS: задатьКартуПресетов, отметитьПресетыПоМодальности,
 *          погаситьПресетыБезИсследования
 */

import { getPreset3D } from '../core/presets3d';
import { log } from '../utils/logger.js';
import { модальностьСерии, доступностьПресета } from '../core/presetModalityCore.js';

let _карта = {};

/** Карта «кнопка → пресет» (uiManager.VRT_PRESET_MAP) — задаётся при запуске. */
export function задатьКартуПресетов(карта) { _карта = карта || {}; }

/** Все кнопки 3D-пресетов: обычные и МР (data-mr3d). [12.0.38] */
const КНОПКИ_3D = '[data-render-preset], [data-mr3d]';
/** Пресеты окна — правая панель и мобильный ящик. [12.0.38] */
const ЧИПЫ_ОКНА = '.preset-chip[data-ww], .preset-chip[data-wl-auto]';
/**
 * [12.0.48] Сценарии (окно + 3D-пресет) — правая панель и мобильный ящик.
 * Модальность берётся из реестра сценариев: _bindСценарии кладёт её в
 * data-wf-modality ('CT', 'MR'). Раньше сценарии не гасли вовсе: «КТ Кости»
 * жалась на МРТ и клала окно 2500/480 на МР-интенсивности, а без
 * исследования сообщала «окно применено», не применив ничего.
 */
const СЦЕНАРИИ = '[data-workflow], [data-workflow-m]';
const _модальностьСценария = (к) => ({ CT: 'ct', MR: 'mri' }[String(к?.dataset?.wfModality || '').toUpperCase()] || 'any');

function _модальностьКнопки(кнопка) {
  // [12.0.38] МР-кнопки — пресеты МРТ по назначению (applyMR3DPreset).
  if (кнопка?.dataset?.mr3d) return 'mri';
  const ключ = кнопка?.dataset?.renderPreset;
  try {
    const cs3d = _карта[ключ]?.cs3d || ключ;
    const пресет = getPreset3D(cs3d) || getPreset3D(ключ);
    return пресет?.modality || null;
  } catch (e) {
    return null;
  }
}

/**
 * Погасить или вернуть каждую кнопку пресета по правилу ядра.
 * @param {'ct'|'mri'|'иное'|'неизвестно'|null} серия — null: исследования нет
 * @param {string} что — для журнала
 * @returns {number} сколько кнопок погашено
 */
function _отметить(серия, что) {
  let погашено = 0;
  const кнопки = [
    ...Array.from(document.querySelectorAll(КНОПКИ_3D), (к) => [к, _модальностьКнопки(к)]),
    // Пресеты окна: без модальности — доступны при любой открытой серии.
    ...Array.from(document.querySelectorAll(ЧИПЫ_ОКНА), (к) => [к, 'any']),
    ...Array.from(document.querySelectorAll(СЦЕНАРИИ), (к) => [к, _модальностьСценария(к)]),
  ];
  for (const [кнопка, модальность] of кнопки) {
    if (!('titleOrig' in кнопка.dataset)) кнопка.dataset.titleOrig = кнопка.getAttribute('title') || '';
    const р = доступностьПресета(модальность, серия);
    кнопка.disabled = !р.доступен;
    кнопка.classList.toggle('preset-off', !р.доступен);
    кнопка.setAttribute('aria-disabled', String(!р.доступен));
    const исходная = кнопка.dataset.titleOrig;
    if (р.доступен) {
      if (исходная) кнопка.setAttribute('title', исходная); else кнопка.removeAttribute('title');
    } else {
      кнопка.setAttribute('title', исходная ? `${р.причина}. ${исходная}` : р.причина);
      // Погашенная кнопка не может оставаться «выбранной»: подсветка
      // осталась бы от закрытого исследования.
      кнопка.classList.remove('active');
      if (кнопка.hasAttribute('aria-pressed')) кнопка.setAttribute('aria-pressed', 'false');   // чипы окна
      погашено++;
    }
  }
  log.info(`[Пресеты 3D] ${что}: погашено кнопок ${погашено}`);
  return погашено;
}

/**
 * Серия открыта: погасить пресеты, не подходящие ей; вернуть подходящие.
 * @param {string|null|undefined} dicomМодальность — «CT», «MR»…; пусто — модальность
 *   не записана (НЕ «серии нет»: тогда не гасим ничего)
 * @returns {number} сколько кнопок погашено
 */
export function отметитьПресетыПоМодальности(dicomМодальность) {
  return _отметить(модальностьСерии(dicomМодальность), `серия ${dicomМодальность || 'без тега модальности'}`);
}

/**
 * [12.0.35] Исследования нет (запуск программы, исследование закрыто): все
 * пресеты серые и неактивные — применять их не к чему.
 * @returns {number} сколько кнопок погашено
 */
export function погаситьПресетыБезИсследования() {
  return _отметить(null, 'исследование не открыто');
}
